// Shared size-guide table handling for the PDP size guide modal and the size guide page.
// Brand charts come from legacy Shopify page content; they are rebuilt into the theme's
// accessible `.size-guide-table` markup, and region columns are tagged for the region filter.
(() => {
  if (window.WobSizeGuideTables) return;

  const getRegionTokens = (value) => {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  };

  const getRegions = (value) => {
    const tokens = getRegionTokens(value);
    const normalized = tokens.join(' ');
    const regions = new Set();

    if (tokens.includes('us') || tokens.includes('usa') || normalized.includes('united states')) regions.add('us');
    if (tokens.includes('aus') || tokens.includes('au') || tokens.includes('australia') || tokens.includes('australian')) regions.add('aus');
    if (tokens.includes('uk') || tokens.includes('gb') || tokens.includes('britain') || normalized.includes('united kingdom')) regions.add('uk');
    if (tokens.includes('eur') || tokens.includes('eu') || tokens.includes('euro') || tokens.includes('europe') || tokens.includes('european')) regions.add('eur');

    return Array.from(regions);
  };

  const getCellRegions = (cell) => {
    const regions = new Set();

    [cell?.dataset?.country, cell?.dataset?.sizeGuideRegion, cell?.className, cell?.textContent].forEach((value) => {
      getRegions(value).forEach((region) => regions.add(region));
    });

    return Array.from(regions);
  };

  const getCellText = (cell) => cell.textContent?.replace(/\s+/g, ' ').trim() || '';

  const normalizeContent = (panel) => {
    panel.classList.add('product-size-guide-modal__panel-content--normalized');

    panel.querySelectorAll('[style]').forEach((element) => {
      if (!(element instanceof HTMLElement) || element.closest('svg')) return;
      if (/display\s*:\s*none/i.test(element.getAttribute('style') || '')) return;

      element.removeAttribute('style');
    });

    // Legacy pages ship their own country <select>; the theme region filter replaces it.
    panel.querySelectorAll('[data-size-guide-country], #country_wrapper, .country_Selector').forEach((selector) => {
      const selectorWrap = selector.closest('.order-msg, .selectdiv') || selector;
      if (selectorWrap instanceof HTMLElement) selectorWrap.hidden = true;
    });

    panel.querySelectorAll('p, div').forEach((element) => {
      if (!(element instanceof HTMLElement) || element.hidden) return;
      if (element.textContent?.trim() || element.querySelector('img, table, iframe, video, svg')) return;
      element.hidden = true;
    });
  };

  const upgradeTable = (table) => {
    const rows = Array.from(table.rows).filter((row) => Array.from(row.cells).some((cell) => getCellText(cell)));
    if (rows.length < 2) return;

    const headerCells = Array.from(rows[0].cells);
    // The first column stays visible on mobile (CSS) because it labels each row.
    const columnRegions = headerCells.map((cell) => getCellRegions(cell));
    const wrap = document.createElement('div');
    const nextTable = document.createElement('table');
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const tbody = document.createElement('tbody');

    wrap.className = 'size-guide-table-wrap';
    nextTable.className = 'size-guide-table';

    if (table.caption && getCellText(table.caption)) {
      const caption = document.createElement('caption');
      caption.textContent = getCellText(table.caption);
      nextTable.appendChild(caption);
    }

    const applyRegion = (cell, index) => {
      if (columnRegions[index]?.length) cell.dataset.sizeGuideRegion = columnRegions[index].join(' ');
    };

    headerCells.forEach((cell, index) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = getCellText(cell) || `Column ${index + 1}`;
      applyRegion(th, index);
      headRow.appendChild(th);
    });

    rows.slice(1).forEach((row) => {
      const nextRow = document.createElement('tr');

      Array.from(row.cells).forEach((cell, index) => {
        const nextCell = document.createElement(index === 0 ? 'th' : 'td');
        if (index === 0) nextCell.scope = 'row';
        nextCell.textContent = getCellText(cell);
        applyRegion(nextCell, index);
        nextRow.appendChild(nextCell);
      });

      tbody.appendChild(nextRow);
    });

    thead.appendChild(headRow);
    nextTable.append(thead, tbody);
    wrap.appendChild(nextTable);
    table.replaceWith(wrap);
  };

  const prepare = (panel) => {
    if (!(panel instanceof HTMLElement) || panel.dataset.sizeGuidePrepared === 'true') return;

    panel.dataset.sizeGuidePrepared = 'true';
    normalizeContent(panel);

    panel.querySelectorAll('table:not(.size-guide-table)').forEach((table) => {
      if (table instanceof HTMLTableElement) upgradeTable(table);
    });
  };

  // Mark region columns so CSS can highlight the selected region on desktop and hide
  // other regions on mobile. Returns whether the panel has any region columns.
  const syncRegion = (container, panel, region) => {
    if (!(container instanceof HTMLElement)) return false;

    container.dataset.sizeGuideActiveRegion = region;

    const regionalCells = Array.from((panel || container).querySelectorAll('.size-guide-table [data-size-guide-region]'));

    regionalCells.forEach((cell) => {
      const regions = String(cell.dataset.sizeGuideRegion || '').split(/\s+/).filter(Boolean);
      const isActive = regions.includes(region);

      cell.classList.toggle('is-active-region', isActive);
      cell.classList.toggle('is-inactive-region', !isActive);
    });

    return regionalCells.length > 0;
  };

  window.WobSizeGuideTables = { prepare, syncRegion };
})();
